import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, Ban, ChevronLeft, Clipboard, ExternalLink, FileText, Package, Pencil, Plus, Printer, Send, ShieldCheck, Trash2, Truck, User, XCircle } from "lucide-react";
import { api, fmtEur, getErrorMessage } from "../../api";
import { downloadAuthedFile } from "../../utils/download";
import PageHeader from "../../components/PageHeader";
import ShipDialog from "../../components/ShipDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import ConfirmDialog from "../../components/ConfirmDialog";
import { OrderStatusBadge } from "./Orders";
import { Badge } from "../../components/ui/badge";

/** Collapse the backend `by` field into a human-readable actor label.
 *  Backend already resolves legacy admin:<uuid> → admin:<email> on load,
 *  but we keep a graceful render fallback for any UUID that slipped through.
 */
function formatActor(by) {
  if (!by) return "";
  if (by.startsWith("admin:")) {
    const tail = by.slice(6);
    // Still a raw UUID? Fall back to just "admin" so we never show a UUID.
    if (!tail.includes("@") && /^[0-9a-f-]{20,}$/i.test(tail)) return "admin";
    return tail;
  }
  if (by.startsWith("buyer:")) return by.slice(6);
  if (by.startsWith("stripe")) return "Stripe";
  if (by === "system") return "System";
  if (by === "admin") return "admin";
  return by;
}

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const [cancelDlg, setCancelDlg] = useState(false);
  const [cancelNote, setCancelNote] = useState("");
  const [confirmDlg, setConfirmDlg] = useState(false);
  const [shipDlg, setShipDlg] = useState(false);
  const [shipMode, setShipMode] = useState("ship"); // "ship" | "edit-tracking"
  const [shipBusy, setShipBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/orders/${id}`);
    setOrder(data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!order) return <div className="p-6 text-[#5C665A]" data-testid="admin-order-loading">Loading…</div>;

  const readOnly = ["shipped", "cancelled"].includes(order.status);

  const patch = async (body) => {
    try { const { data } = await api.patch(`/admin/orders/${order.id}`, body); setOrder(data); }
    catch (err) { toast.error(getErrorMessage(err, "Failed")); }
  };

  const setLineQty = async (line_id, quantity) => {
    if (quantity <= 0) {
      try { const { data } = await api.delete(`/admin/orders/${order.id}/lines/${line_id}`); setOrder(data); toast.success("Line removed"); }
      catch (err) { toast.error(getErrorMessage(err, "Failed")); }
      return;
    }
    try { const { data } = await api.patch(`/admin/orders/${order.id}/lines/${line_id}`, { quantity }); setOrder(data); }
    catch (err) { toast.error(getErrorMessage(err, "Failed")); }
  };

  const addLine = async (size_variant_id, quantity) => {
    try { const { data } = await api.post(`/admin/orders/${order.id}/lines`, { size_variant_id, quantity }); setOrder(data); toast.success("Line added"); }
    catch (err) { toast.error(getErrorMessage(err, "Failed")); }
  };

  const transition = async (action, body = null) => {
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/${action}`, body || {});
      setOrder(data);
      setConflicts(null);
      toast.success(`Order ${data.status}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Structured "stock_conflicts" payload (dict with list) — never a validation array
      if (detail && typeof detail === "object" && !Array.isArray(detail) && detail.stock_conflicts) {
        setConflicts(detail.stock_conflicts);
        toast.error("Stock conflicts — resolve to confirm");
      } else {
        toast.error(getErrorMessage(err, "Failed"));
      }
    }
  };

  const openShipDialog = () => { setShipMode("ship"); setShipDlg(true); };
  const openEditTracking = () => { setShipMode("edit-tracking"); setShipDlg(true); };

  const submitShip = async (payload) => {
    setShipBusy(true);
    try {
      if (shipMode === "edit-tracking") {
        const { data } = await api.patch(`/admin/orders/${order.id}/tracking`, payload);
        setOrder(data);
        toast.success("Tracking updated");
      } else {
        // Ship — tracking is OPTIONAL, we still call the endpoint with the (possibly all-null) payload.
        const { data } = await api.post(`/admin/orders/${order.id}/ship`, payload);
        setOrder(data);
        toast.success(payload.tracking_number ? "Shipped with tracking" : "Shipped (no tracking)");
      }
      setShipDlg(false);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed"));
    } finally {
      setShipBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={<span className="db-mono">{order.order_number}</span>}
        subtitle={
          <span className="inline-flex items-center gap-2">
            {order.is_guest
              ? <><User size={12} /> {order.guest_name || "Walk-in"}{order.guest_email ? ` · ${order.guest_email}` : ""} · {new Date(order.created_at).toLocaleString()}</>
              : <>{order.buyer?.first_name} {order.buyer?.last_name} · {order.buyer?.company || "—"} · {new Date(order.created_at).toLocaleString()}</>}
            {order.invoice_number && (
              <span className="db-mono text-xs px-1.5 py-0.5 rounded bg-[#E9F5EC] text-[#1B4332]" data-testid="admin-order-invoice-badge">
                {order.invoice_number}
              </span>
            )}
          </span>
        }
        testid="page-admin-order-detail"
        actions={
          <>
            <Link to="/admin/orders"><Button variant="outline" className="h-9"><ChevronLeft size={14} /> Orders</Button></Link>
            {order.invoice_number && (
              <Button
                variant="outline"
                className="h-9"
                data-testid="admin-order-invoice-download"
                onClick={() => downloadAuthedFile(
                  `/api/admin/orders/${order.id}/invoice.pdf`,
                  `${order.invoice_number}.pdf`,
                  () => toast.error("Invoice download failed"),
                )}
              >
                <FileText size={14} /> Invoice PDF
              </Button>
            )}
            <Button
              variant="outline"
              className="h-9"
              data-testid="admin-order-pro-forma-download"
              onClick={() => downloadAuthedFile(
                `/api/admin/orders/${order.id}/pro-forma.pdf`,
                `PF-${order.invoice_number || order.order_number}.pdf`,
                () => toast.error("Pro forma download failed"),
              )}
            >
              <FileText size={14} /> Pro forma invoice
            </Button>
            {order.payment_status === "paid" && (
              <span
                className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-[#1B4332] text-white text-sm font-medium"
                data-testid="admin-order-paid-badge"
                title={order.paid_offline ? "Marked paid offline" : "Paid via Stripe"}
              >
                <ShieldCheck size={14} /> PAID{order.paid_offline ? " (offline)" : ""}
              </span>
            )}
            {order.payment_status !== "paid" && order.stripe_session_id && (
              <Button
                variant="outline"
                className="h-9"
                onClick={async () => {
                  try { await api.post(`/admin/orders/${order.id}/check-payment`); load(); toast.success("Status refreshed"); }
                  catch (e) { toast.error(getErrorMessage(e, "Failed")); }
                }}
                data-testid="admin-order-check-payment"
              >Check payment</Button>
            )}
            {order.payment_status !== "paid" && ["confirmed","packed","shipped"].includes(order.status) && (
              <Button
                variant="outline"
                className="h-9"
                onClick={async () => {
                  try { await api.post(`/admin/orders/${order.id}/mark-paid`); load(); toast.success("Marked paid (offline)"); }
                  catch (e) { toast.error(getErrorMessage(e, "Failed")); }
                }}
                data-testid="admin-order-mark-paid"
              >Mark as paid (offline)</Button>
            )}
            <a href={`/admin/orders/${order.id}/packing-list`} target="_blank" rel="noreferrer" data-testid="admin-order-packing-list">
              <Button variant="outline" className="h-9"><Printer size={14} /> Packing list</Button>
            </a>
          </>
        }
      />
      <div className="p-4 sm:p-6 grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4 sm:gap-6 max-w-7xl">
        {/* Left: lines */}
        <div className="space-y-4">
          {conflicts && (
            <div className="border border-[#f2c7c7] bg-[#FDF3F3] text-[#C94A4A] rounded-md p-3" data-testid="admin-order-conflicts">
              <div className="flex items-center gap-2 db-h font-semibold text-sm"><AlertTriangle size={16} /> Stock conflicts</div>
              <ul className="mt-1 text-xs list-disc pl-5">
                {conflicts.map((c) => {
                  const oos = Number(c.available) === 0;
                  return (
                    <li key={c.line_id} data-testid={`admin-order-conflict-${c.line_id}`}>
                      {c.product_name} · {c.size_name}:{" "}
                      {oos ? (
                        <span className="font-semibold" data-testid={`admin-order-conflict-oos-${c.line_id}`}>
                          out of stock
                          <span className="text-[#C94A4A]/80 font-normal"> — requested <span className="db-mono">{c.requested}</span></span>
                        </span>
                      ) : (
                        <>requested <span className="db-mono">{c.requested}</span>, available <span className="db-mono">{c.available}</span></>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <BuyerBlock buyer={order.buyer} email={order.buyer_email} order={order} />

          <div className="bg-white border border-[#E2E4E0] rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-[#F9F9F8] text-[#5C665A]">
                <tr>
                  <th className="text-left px-3 py-2 db-h font-semibold">Line</th>
                  <th className="text-right px-3 py-2 db-h font-semibold w-28">Unit</th>
                  <th className="text-center px-3 py-2 db-h font-semibold w-24">Qty</th>
                  <th className="text-right px-3 py-2 db-h font-semibold w-24">Available</th>
                  <th className="text-right px-3 py-2 db-h font-semibold w-28">Line total</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {order.lines.map((l) => (
                  <LineRow key={l.id} line={l} readOnly={readOnly} orderStatus={order.status} stockDecremented={order.stock_decremented} onQty={(q) => setLineQty(l.id, q)} onRemove={() => setLineQty(l.id, 0)} />
                ))}
                {order.lines.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-[#5C665A]">No lines.</td></tr>}
              </tbody>
            </table>
            </div>
          </div>

          {!readOnly && <AddLineForm onAdd={addLine} />}

          <div className="bg-white border border-[#E2E4E0] rounded-lg p-4">
            <h3 className="db-h font-semibold mb-2">Admin note (internal)</h3>
            <Textarea data-testid="admin-order-note" defaultValue={order.admin_note || ""} onBlur={(e) => e.target.value !== (order.admin_note || "") && patch({ admin_note: e.target.value })} placeholder="Internal notes" rows={2} readOnly={readOnly} key={order.id} />
          </div>
        </div>

        {/* Right: totals + status */}
        <div className="space-y-4">
          <TotalsPanel order={order} onPatch={patch} readOnly={readOnly} />

          <div className="bg-white border border-[#E2E4E0] rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="db-h font-semibold text-sm">Status</span>
              <OrderStatusBadge status={order.status} />
            </div>
            <div className="mt-3 space-y-2">
              {order.status === "pending" && (
                <Button data-testid="admin-order-confirm" className="w-full h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white" onClick={() => setConfirmDlg(true)}>
                  <ShieldCheck size={14} /> Confirm & decrement stock
                </Button>
              )}
              {order.status === "confirmed" && (
                <>
                  <Button data-testid="admin-order-pack" className="w-full h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white" onClick={() => transition("pack")}>
                    <Package size={14} /> Mark packed
                  </Button>
                  <Button data-testid="admin-order-ship-from-confirmed" variant="outline" className="w-full h-9" onClick={openShipDialog}>
                    <Truck size={14} /> Skip to shipped
                  </Button>
                </>
              )}
              {order.status === "packed" && (
                <Button data-testid="admin-order-ship" className="w-full h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white" onClick={openShipDialog}>
                  <Truck size={14} /> Mark shipped
                </Button>
              )}
              {order.status === "shipped" && (
                <Button data-testid="admin-order-edit-tracking" variant="outline" className="w-full h-9" onClick={openEditTracking}>
                  <Pencil size={14} /> {order.tracking_number ? "Edit tracking" : "Add tracking"}
                </Button>
              )}
              {["pending", "confirmed", "packed"].includes(order.status) && (
                <Button data-testid="admin-order-cancel" variant="outline" className="w-full h-9 text-[#C94A4A] border-[#f2c7c7] hover:bg-[#FDF3F3]" onClick={() => setCancelDlg(true)}>
                  <Ban size={14} /> Cancel order
                </Button>
              )}
            </div>

            <div className="mt-4 border-t border-[#E2E4E0] pt-3">
              <div className="text-xs text-[#5C665A] db-h font-semibold uppercase mb-1">History</div>
              {/* Payment step — first-class row between confirmed and packed.
                  Only rendered after the order has been confirmed; cancelled
                  orders skip it entirely. Shows a prominent chip for the
                  current state (paid vs awaiting) so the owner never has to
                  dig for payment status. */}
              {["confirmed", "packed", "shipped"].includes(order.status) && (
                <div className="mb-2 p-2 rounded-md border bg-[#F9F9F8] border-[#E2E4E0]" data-testid="admin-order-payment-step">
                  <div className="flex items-center justify-between">
                    <div className="db-h font-semibold text-xs uppercase text-[#5C665A]">Payment</div>
                    {order.payment_status === "paid" ? (
                      <Badge className="bg-[#E9F5EC] text-[#1B4332] border-transparent inline-flex items-center gap-1" data-testid="admin-order-payment-paid">
                        <ShieldCheck size={11} /> Paid{order.paid_offline ? " · offline" : " · Stripe"}
                      </Badge>
                    ) : (
                      <Badge className="bg-[#FBF3E8] text-[#8A5A2B] border border-[#D4A373] inline-flex items-center gap-1" data-testid="admin-order-payment-awaiting">
                        <AlertTriangle size={11} /> Awaiting payment
                      </Badge>
                    )}
                  </div>
                  {order.payment_status === "paid" && order.paid_at && (
                    <div className="text-[11px] text-[#5C665A] mt-0.5" data-testid="admin-order-paid-at">
                      {new Date(order.paid_at).toLocaleString()}
                    </div>
                  )}
                  {order.payment_status !== "paid" && (
                    <div className="mt-1.5 space-y-1.5">
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-[#2D6A4F] hover:bg-[#1B4332] text-white"
                          onClick={async () => {
                            try { await api.post(`/admin/orders/${order.id}/mark-paid`); load(); toast.success("Marked paid (offline)"); }
                            catch (e) { toast.error(getErrorMessage(e, "Failed")); }
                          }}
                          data-testid="admin-order-payment-mark-paid"
                        >
                          <ShieldCheck size={12} /> Mark paid (offline)
                        </Button>
                        {order.stripe_session_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={async () => {
                              try { await api.post(`/admin/orders/${order.id}/check-payment`); load(); toast.success("Status refreshed"); }
                              catch (e) { toast.error(getErrorMessage(e, "Failed")); }
                            }}
                            data-testid="admin-order-payment-check"
                          >Check Stripe</Button>
                        )}
                      </div>
                      {order.stripe_payment_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs w-full justify-start"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(order.stripe_payment_url);
                              toast.success("Payment link copied — paste into WhatsApp/email");
                            } catch {
                              // Fallback for older browsers / insecure contexts.
                              const ta = document.createElement("textarea");
                              ta.value = order.stripe_payment_url;
                              document.body.appendChild(ta);
                              ta.select();
                              try { document.execCommand("copy"); toast.success("Payment link copied"); }
                              catch { toast.error("Copy failed — copy manually from the field below"); }
                              finally { document.body.removeChild(ta); }
                            }
                          }}
                          title={order.stripe_payment_url}
                          data-testid="admin-order-copy-payment-link"
                        >
                          <Clipboard size={12} /> Copy payment link
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
              <ol className="space-y-1 text-xs">
                {(order.status_history || []).map((s, i) => (
                  <li key={i} className="flex justify-between gap-2" data-testid={`admin-order-history-${i}`}>
                    <span className="capitalize">{s.status}</span>
                    <span className="text-[#5C665A] text-right">
                      {new Date(s.at).toLocaleString()}
                      {s.by && (
                        <>
                          {" · "}
                          <span data-testid={`admin-order-history-by-${i}`}>{formatActor(s.by)}</span>
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
              {order.status === "confirmed" && order.stock_decremented && (
                <div className="mt-2 text-xs text-[#5C665A]">Stock decremented for this order.</div>
              )}
            </div>
            {order.tracking_number && (
              <div className="mt-3 border-t border-[#E2E4E0] pt-3 text-xs" data-testid="admin-order-tracking-block">
                <div className="text-[#5C665A] db-h font-semibold uppercase mb-0.5">Tracking</div>
                <div className="db-mono text-sm text-[#1A1D1A]">
                  {order.tracking_number}
                  {order.tracking_carrier && <span className="text-[#5C665A] ml-1">· {order.tracking_carrier}</span>}
                </div>
                {order.tracking_url && (
                  <a href={order.tracking_url} target="_blank" rel="noreferrer" className="text-[#2D6A4F] hover:underline inline-flex items-center gap-1 mt-0.5" data-testid="admin-order-tracking-url">
                    Open tracking page <ExternalLink size={10} />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <ShipDialog
        open={shipDlg}
        onOpenChange={setShipDlg}
        mode={shipMode}
        initial={shipMode === "edit-tracking" ? {
          tracking_number: order.tracking_number,
          tracking_carrier: order.tracking_carrier,
          tracking_url: order.tracking_url,
        } : null}
        submitting={shipBusy}
        onSubmit={submitShip}
      />

      <ConfirmDialog
        open={cancelDlg}
        onOpenChange={(o) => !o && setCancelDlg(false)}
        title="Cancel this order?"
        description={order.stock_decremented ? "Stock will be restored via the inventory service." : "This order has not touched stock yet."}
        confirmLabel="Cancel order"
        destructive
        onConfirm={() => { transition("cancel", { note: cancelNote }); setCancelDlg(false); setCancelNote(""); }}
        testid="admin-order-confirm-cancel"
      />

      <ConfirmDialog
        open={confirmDlg}
        onOpenChange={(o) => !o && setConfirmDlg(false)}
        title={`Confirm order ${order.order_number}?`}
        description={
          <span data-testid="admin-order-confirm-summary">
            Stock will be decremented via the inventory service. The invoice
            &amp; Stripe payment link are generated NOW — costs set after this
            point won&apos;t appear in the confirmation email.<br />
            Subtotal: <b>{fmtEur(order.subtotal)}</b>
            {order.discount_amount > 0 && <> · Discount: <b>−{fmtEur(order.discount_amount)}</b></>}
            <> · Shipping: <b>{fmtEur(order.shipping_cost || 0)}</b></>
            <> · Phyto: <b>{fmtEur(order.phyto_cost || 0)}</b></>
            <> · Total: <b>{fmtEur(order.total)}</b></>
            {(!order.shipping_cost || Number(order.shipping_cost) === 0) && (
              <span className="block mt-2 text-[#D4A373]" data-testid="admin-order-confirm-warn-shipping">
                Shipping is 0.00 — set it in the Totals panel first if this order ships.
              </span>
            )}
            {(!order.phyto_cost || Number(order.phyto_cost) === 0) && (
              <span className="block mt-1 text-[#D4A373]" data-testid="admin-order-confirm-warn-phyto">
                Phytosanitary is 0.00 — set it in the Totals panel first if this order needs a phyto certificate.
              </span>
            )}
          </span>
        }
        confirmLabel="Confirm & decrement stock"
        onConfirm={() => { transition("confirm"); setConfirmDlg(false); }}
        testid="admin-order-confirm-dialog"
      />
    </>
  );
}

function BuyerBlock({ buyer, email, order }) {
  const shipTo = order?.shipping_address;
  const shipToHasContent = shipTo && (shipTo.street_address || shipTo.city);
  if (order?.is_guest) {
    return (
      <div className="bg-white border border-[#E2E4E0] rounded-lg p-4 text-sm" data-testid="admin-order-guest">
        <div className="flex items-start justify-between">
          <div>
            <div className="db-h font-semibold inline-flex items-center gap-1.5">
              <User size={14} /> {order.guest_name || "Walk-in"}
              <Badge className="bg-neutral-100 text-neutral-600 border-transparent text-[10px] px-1 ml-1">Guest</Badge>
            </div>
            <div className="text-xs text-[#5C665A]">{order.guest_email || "no email on file"}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 mt-2">
          <Field label="Country" value={order.guest_country_code} />
        </div>
        {shipToHasContent && <ShipToBlock shipTo={shipTo} />}
      </div>
    );
  }
  if (!buyer) return null;
  return (
    <div className="bg-white border border-[#E2E4E0] rounded-lg p-4 text-sm" data-testid="admin-order-buyer">
      <div className="flex items-start justify-between">
        <div>
          <div className="db-h font-semibold">
            <Link to={`/admin/buyers/${buyer.id}`} className="hover:underline" data-testid="admin-order-buyer-link">
              {buyer.first_name} {buyer.last_name}
            </Link>
          </div>
          <div className="text-xs text-[#5C665A]">{email}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 mt-2">
        <Field label="Company" value={buyer.company} />
        <Field label="Phone" value={buyer.phone} />
        <Field label="VAT" value={buyer.vat_id} />
        <Field label="Import licence" value={buyer.import_license ? (buyer.import_license_number || "yes") : "no"} />
      </div>
      {shipToHasContent && <ShipToBlock shipTo={shipTo} />}
    </div>
  );
}

function ShipToBlock({ shipTo }) {
  return (
    <div className="mt-3 pt-3 border-t border-[#E2E4E0]" data-testid="admin-order-shipping">
      <div className="text-[10px] text-[#5C665A] uppercase tracking-wider mb-0.5">Ship to</div>
      <address className="not-italic text-sm text-[#1A1D1A]">
        {shipTo.name && <>{shipTo.name}<br /></>}
        {shipTo.company && <>{shipTo.company}<br /></>}
        {shipTo.street_address}
        {shipTo.address_line2 && <>, {shipTo.address_line2}</>}<br />
        {[shipTo.postal_code, shipTo.city].filter(Boolean).join(" ")}
        {shipTo.country_code && <>, {shipTo.country_code}</>}
        {shipTo.phone && <><br /><span className="text-[#5C665A] text-xs">Phone: {shipTo.phone}</span></>}
      </address>
    </div>
  );
}
function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] text-[#5C665A] uppercase tracking-wider">{label}</div>
      <div className="text-sm">{value || "—"}</div>
    </div>
  );
}

function LineRow({ line, readOnly, orderStatus, stockDecremented, onQty, onRemove }) {
  // Available column: only meaningful before stock decrement.
  // - Post-confirm/pack/ship (stock already consumed): muted display, no compare.
  // - Pending: green if stock covers the qty, red if short.
  // Zero stock is special-cased so the owner sees it instantly during confirm —
  // the demo showed a confirm was blocked by a 0-stock line the owner missed.
  const consumed = !!stockDecremented || ["shipped", "cancelled"].includes(orderStatus);
  const available = line.current_stock_qty;
  const outOfStock = !consumed && available != null && Number(available) === 0;
  const short = !consumed && !outOfStock && available != null && available < Number(line.quantity || 0);
  const availableCls = consumed
    ? "text-[#5C665A]"
    : outOfStock
      ? "text-[#C94A4A] font-bold"
      : short
        ? "text-[#C94A4A] font-semibold"
        : "text-[#2D6A4F]";
  const rowCls = outOfStock
    ? "border-t border-[#F2C7C7] bg-[#FDF3F3]"
    : "border-t border-[#E2E4E0]";
  return (
    <tr className={`${rowCls} db-row`} data-testid={`admin-line-${line.id}`} data-out-of-stock={outOfStock || undefined}>
      <td className="px-3 py-2">
        <div className="font-medium break-words inline-flex items-center gap-2">
          {outOfStock && <XCircle size={14} className="text-[#C94A4A] shrink-0" data-testid={`admin-line-oos-icon-${line.id}`} />}
          <span>{line.product_name} <span className="text-[#5C665A]">· {line.size_name}</span></span>
        </div>
        <div className="text-xs text-[#5C665A] db-mono">{line.sku}</div>
        {line.more_requested && (
          <span
            className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wide bg-[#FBF7E6] text-[#8A7A2E] border border-[#EFE1A6] rounded px-1.5 py-0.5"
            data-testid={`admin-line-more-requested-${line.id}`}
            title="Buyer used 'Looking for more?' to request beyond the banded display"
          >
            More requested
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right db-mono">{fmtEur(line.unit_price)}</td>
      <td className="px-3 py-2">
        {readOnly ? (
          <div className="text-center db-mono">{line.quantity}</div>
        ) : (
          <Input type="number" min={1} defaultValue={line.quantity} onBlur={(e) => Number(e.target.value) !== line.quantity && onQty(Number(e.target.value))} className="h-8 text-center db-mono" data-testid={`admin-line-qty-${line.id}`} />
        )}
      </td>
      <td className={`px-3 py-2 text-right db-mono ${availableCls}`} data-testid={`admin-line-available-${line.id}`}>
        {available == null ? "—" : available}
        {outOfStock && (
          <div className="text-[11px] font-semibold text-[#C94A4A] inline-flex items-center gap-1 justify-end mt-0.5">
            <XCircle size={11} /> out of stock
          </div>
        )}
        {!outOfStock && !consumed && short && (
          <div className="text-[10px] text-[#C94A4A]">short by {Number(line.quantity) - Number(available)}</div>
        )}
        {consumed && (
          <div className="text-[10px] text-[#5C665A]">post-consumption</div>
        )}
      </td>
      <td className="px-3 py-2 text-right db-mono font-semibold">{fmtEur(line.line_total)}</td>
      <td className="px-2 py-2 text-right">
        {!readOnly && <button className="text-[#5C665A] hover:text-[#C94A4A] p-1" onClick={onRemove} data-testid={`admin-line-remove-${line.id}`}><Trash2 size={14} /></button>}
      </td>
    </tr>
  );
}

function AddLineForm({ onAdd }) {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [sizeId, setSizeId] = useState("");
  const [qty, setQty] = useState(1);
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);
  const p = products.find((x) => x.id === productId);
  // Keep variant order but push out-of-stock to the bottom so the picker
  // defaults to a sellable size. Stable sort via indexed decoration.
  const sizes = useMemo(() => {
    const arr = (p?.size_variants || []).map((s, i) => ({ s, i }));
    arr.sort((a, b) => {
      const ao = Number(a.s.stock_qty) === 0 ? 1 : 0;
      const bo = Number(b.s.stock_qty) === 0 ? 1 : 0;
      return ao - bo || a.i - b.i;
    });
    return arr.map(({ s }) => s);
  }, [p]);
  const submit = async () => {
    if (!sizeId || !qty) return;
    await onAdd(sizeId, Number(qty));
    setSizeId(""); setQty(1);
  };
  return (
    <div className="bg-white border border-[#E2E4E0] rounded-lg p-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_100px_auto] gap-2 items-end" data-testid="admin-add-line-form">
      <div>
        <label className="text-xs text-[#5C665A]">Product</label>
        <Select value={productId} onValueChange={(v) => { setProductId(v); setSizeId(""); }}>
          <SelectTrigger className="h-9" data-testid="admin-add-line-product"><SelectValue placeholder="Choose product…" /></SelectTrigger>
          <SelectContent>
            {products.map((pr) => <SelectItem key={pr.id} value={pr.id}>{pr.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs text-[#5C665A]">Size · SKU · stock</label>
        <Select value={sizeId} onValueChange={setSizeId} disabled={!p}>
          <SelectTrigger className="h-9" data-testid="admin-add-line-size"><SelectValue placeholder={p ? "Choose size" : "Pick product first"} /></SelectTrigger>
          <SelectContent>
            {sizes.map((s) => {
              const oos = Number(s.stock_qty) === 0;
              return (
                <SelectItem
                  key={s.id}
                  value={s.id}
                  data-testid={`admin-add-line-size-option-${s.id}`}
                  className={oos ? "text-[#C94A4A]" : ""}
                >
                  <span className="inline-flex items-center gap-1.5">
                    {oos && <XCircle size={12} className="text-[#C94A4A]" />}
                    <span>{s.sku} · stock {s.stock_qty}{oos ? " · out of stock" : ""}</span>
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs text-[#5C665A]">Qty</label>
        <Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} className="h-9 text-center db-mono" data-testid="admin-add-line-qty" />
      </div>
      <Button data-testid="admin-add-line-submit" onClick={submit} className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white"><Plus size={14} /> Add</Button>
    </div>
  );
}

function TotalsPanel({ order, onPatch, readOnly }) {
  const [dType, setDType] = useState(order.discount_type || "__none__");
  const [dVal, setDVal] = useState(order.discount_value || 0);
  const [ship, setShip] = useState(order.shipping_cost || 0);
  const [phyto, setPhyto] = useState(order.phyto_cost || 0);
  const [pfUnit, setPfUnit] = useState(order.pro_forma_unit_value ?? 0.10);
  const savedRef = useRef({ dType: order.discount_type, dVal: order.discount_value, ship: order.shipping_cost, phyto: order.phyto_cost, pfUnit: order.pro_forma_unit_value });

  useEffect(() => {
    setDType(order.discount_type || "__none__");
    setDVal(order.discount_value || 0);
    setShip(order.shipping_cost || 0);
    setPhyto(order.phyto_cost || 0);
    setPfUnit(order.pro_forma_unit_value ?? 0.10);
    savedRef.current = { dType: order.discount_type, dVal: order.discount_value, ship: order.shipping_cost, phyto: order.phyto_cost, pfUnit: order.pro_forma_unit_value };
  }, [order.id, order.discount_type, order.discount_value, order.shipping_cost, order.phyto_cost, order.pro_forma_unit_value]);

  const commit = () => {
    onPatch({
      discount_type: dType === "__none__" ? null : dType,
      discount_value: Number(dVal) || 0,
      shipping_cost: Number(ship) || 0,
      phyto_cost: Number(phyto) || 0,
      pro_forma_unit_value: Number(pfUnit) || 0.10,
    });
  };

  return (
    <div className="bg-white border border-[#E2E4E0] rounded-lg p-4" data-testid="admin-order-totals">
      <h3 className="db-h font-semibold mb-3">Totals</h3>
      <div className="text-sm flex justify-between py-1">
        <span className="text-[#5C665A]">Subtotal</span>
        <span className="db-mono" data-testid="admin-order-subtotal">{fmtEur(order.subtotal)}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 items-end mt-2">
        <div>
          <label className="text-xs text-[#5C665A]">Order discount</label>
          <Select value={dType} onValueChange={(v) => setDType(v)} disabled={readOnly}>
            <SelectTrigger className="h-9" data-testid="admin-order-discount-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              <SelectItem value="percent">Percent</SelectItem>
              <SelectItem value="fixed">Fixed €</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-[#5C665A]">Value</label>
          <Input type="number" step="0.01" value={dVal} onChange={(e) => setDVal(e.target.value)} onBlur={commit} className="h-9 text-right db-mono" data-testid="admin-order-discount-value" disabled={readOnly || dType === "__none__"} />
        </div>
      </div>
      {order.discount_amount > 0 && (
        <div className="text-xs flex justify-between mt-1">
          <span className="text-[#5C665A]">Discount</span>
          <span className="db-mono text-[#C94A4A]" data-testid="admin-order-discount-amount">− {fmtEur(order.discount_amount)}</span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 mt-2">
        <div>
          <label className="text-xs text-[#5C665A]">Shipping</label>
          <Input type="number" step="0.01" value={ship} onChange={(e) => setShip(e.target.value)} onBlur={commit} className="h-9 text-right db-mono" data-testid="admin-order-shipping" disabled={readOnly} />
        </div>
        <div>
          <label className="text-xs text-[#5C665A]">Phytosanitary</label>
          <Input type="number" step="0.01" value={phyto} onChange={(e) => setPhyto(e.target.value)} onBlur={commit} className="h-9 text-right db-mono" data-testid="admin-order-phyto" disabled={readOnly} />
        </div>
      </div>
      <div className="mt-2">
        <label className="text-xs text-[#5C665A]" title="Customs value per piece for the Pro Forma invoice (defaults to €0.10). Does not affect the commercial invoice or Stripe amount.">Pro forma unit value (€)</label>
        <Input
          type="number" step="0.01" min="0.01"
          value={pfUnit}
          onChange={(e) => setPfUnit(e.target.value)}
          onBlur={commit}
          className="h-9 text-right db-mono w-32"
          data-testid="admin-order-pf-unit"
          disabled={readOnly}
        />
      </div>
      <div className="mt-3 pt-3 border-t border-[#E2E4E0] flex justify-between">
        <span className="db-h font-semibold">Total</span>
        <span className="db-mono text-xl font-bold" data-testid="admin-order-total">{fmtEur(order.total)}</span>
      </div>
      <button data-testid="admin-order-totals-commit" onClick={commit} className="mt-2 text-xs text-[#2D6A4F] hover:underline inline-flex items-center gap-1" disabled={readOnly}>
        <Send size={12} /> Apply changes
      </button>
    </div>
  );
}
